import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { RgpdConsent } from "../components/RgpdConsent";
import { PublicFooter } from "../components/PublicFooter";
import { CheckCircle2, Loader2, Send, ShieldCheck, Sparkles, ArrowRight, LogIn, Camera, FileText } from "lucide-react";
import { DateInput } from "../components/DateInput";
import { usePublicForm, Unavailable, OwnershipNote } from "../components/PublicFormBits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Diploma({ code, label, value, onChange }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white">
      <div className="text-sm font-medium mb-2">{label}</div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-1 text-sm">
          <input type="radio" name={`d-${code}`} checked={value.has === true} onChange={()=>onChange({ has: true, expires_on: value.expires_on || "" })} /> Oui
        </label>
        <label className="flex items-center gap-1 text-sm">
          <input type="radio" name={`d-${code}`} checked={value.has === false} onChange={()=>onChange({ has: false, expires_on: "" })} /> Non
        </label>
        {value.has && (
          <label className="flex items-center gap-2 text-xs text-slate-700 ml-auto">
            <span>Date d'expiration</span>
            <DateInput
              data-testid={`d-${code}-expires`}
              className="border border-slate-300 rounded px-2 py-1 text-sm"
              value={value.expires_on || ""}
              onChange={(e)=>onChange({ has: true, expires_on: e.target.value })}
            />
          </label>
        )}
      </div>
    </div>
  );
}

export default function ApplyCandidate() {
  const [brand, setBrand] = useState({ name: "", primary_color: "#0284c7", brand_tagline: "", logo_file_id: null });
  const [form, setForm] = useState({
    civility: "M.",
    first_name: "", last_name: "", email: "", phone: "", birth_date: "",
    address: "", postal_code: "", city: "",
    carte_pro_number: "", photo_file_id: null, cv_file_id: null,
    diplomas: {
      SSIAP1: { has: false, expires_on: "" },
      SSIAP2: { has: false, expires_on: "" },
      SSIAP3: { has: false, expires_on: "" },
      SST:    { has: false, expires_on: "" },
    },
    message: "",
    rgpd_consent: false,
  });
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const pf = usePublicForm("apply");

  useEffect(() => {
    axios.get(`${API}/brand`, { headers: pf.headers }).then((r)=>setBrand((b) => ({ ...b, ...r.data }))).catch(()=>{});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pf.link]);

  const setD = (code, v) => setForm({ ...form, diplomas: { ...form.diplomas, [code]: v } });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.first_name.trim() || !form.last_name.trim()) return setErr("Nom et prénom obligatoires.");
    if (!form.email.trim() || !form.phone.trim()) return setErr("Email et téléphone obligatoires.");
    if (!form.birth_date) return setErr("Date de naissance obligatoire.");
    if (!form.address.trim() || !form.postal_code.trim() || !form.city.trim()) return setErr("Adresse postale, code postal et ville obligatoires.");
    if (!form.carte_pro_number.trim()) return setErr("Numéro de carte professionnelle obligatoire.");
    if (!CARTE_PRO_RE.test(form.carte_pro_number.trim().toUpperCase())) return setErr("Numéro de carte professionnelle invalide. Formats acceptés : 1234-1234567-CAR-SH-1234567 ou CAR-123-1234-12-12-12341234567");
    if (!form.photo_file_id) return setErr("Merci de joindre une photo d'identité.");
    for (const [code, d] of Object.entries(form.diplomas)) {
      if (d.has && !d.expires_on) return setErr(`Merci d'indiquer la date d'expiration pour ${code}.`);
    }
    if (!form.rgpd_consent) return setErr("Merci d'accepter le traitement de vos données personnelles.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/candidates/apply`, form, { headers: pf.headers });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur lors de l'envoi. Merci de réessayer.");
    } finally { setSubmitting(false); }
  };

  const color = brand.primary_color || "#0284c7";
  const logoUrl = brand.logo_file_id ? `${API}/brand/logo${pf.logoQs}` : null;

  if (pf.available === false) return <Unavailable info={pf.info} />;

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-100 p-6" style={{ "--brand-primary": color }}>
        <div className="max-w-md w-full bg-white rounded-md border border-emerald-200 p-8 text-center shadow-sm">
          <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600 mb-3" />
          <h1 className="font-display text-2xl font-extrabold text-slate-900">Candidature envoyée !</h1>
          <p className="text-sm text-slate-600 mt-2">
            Merci <b>{form.first_name}</b>, votre candidature a bien été transmise à l'équipe {brand.name}.
          </p>
          <p className="text-xs text-slate-500 mt-1">Nous vous recontacterons rapidement.</p>
          {!pf.link && <Link to="/" className="inline-block mt-4 text-xs font-semibold" style={{ color }}>← Retour à l'accueil</Link>}
          <OwnershipNote info={pf.info} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-16" style={{ "--brand-primary": color }} data-testid="apply-page">
      {/* Barre supérieure : logo + accès admin discret */}
      <div className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {logoUrl && <img src={logoUrl} alt="logo" className="h-12 object-contain" />}
            <div>
              <div className="font-display font-bold text-xl" style={{ color }}>{brand.name}</div>
              <div className="text-[11px] text-slate-500">{brand.brand_tagline || brand.tagline || "Rejoignez notre équipe"}</div>
            </div>
          </div>
          {!pf.link && <Link
            to="/login"
            data-testid="admin-access-link"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50"
          >
            <LogIn className="h-3.5 w-3.5" /> Accès administrateur
          </Link>}
        </div>
      </div>

      {/* HERO */}
      <div className="relative overflow-hidden" style={{ backgroundColor: "#0B1E3F" }}>
        <div className="absolute inset-0 opacity-25" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&q=80&w=1600')",
          backgroundSize: "cover", backgroundPosition: "center",
          filter: "grayscale(30%) brightness(0.7)"
        }} />
        <div className="absolute inset-0" style={{
          background: "linear-gradient(135deg, rgba(11,30,63,0.92) 0%, rgba(11,30,63,0.55) 60%, rgba(15,23,42,0.75) 100%)"
        }} />
        <div className="relative max-w-5xl mx-auto px-6 py-14 text-white">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/25 backdrop-blur-md rounded-full px-3 py-1.5 mb-5">
            <Sparkles className="h-3.5 w-3.5" style={{ color: "#93C5FD" }} />
            <span className="text-[11px] font-semibold tracking-wide uppercase">On recrute maintenant</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-4 gold-wave">
            Rejoignez l'équipe {brand.name}.
          </h1>
          <p className="text-white/85 text-base max-w-xl mb-6">
            Postes de <b className="text-white">SSIAP</b>, <b className="text-white">ADS</b>, <b className="text-white">chef d'équipe</b>, <b className="text-white">agent cynophile</b>… déposez votre candidature en 2 minutes, sans compte ni CV à joindre.
          </p>
          <a
            href="#formulaire"
            className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold shadow-xl transition-all hover:-translate-y-0.5"
            style={{ backgroundColor: color, color: "#fff" }}
          >
            Commencer ma candidature <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-6" id="formulaire">
        <div className="bg-white rounded-md border border-slate-200 shadow-sm p-6">
          <div className="mb-5">
            <h2 className="font-display text-2xl font-extrabold" style={{ color }}>Postuler chez {brand.name}</h2>
            <p className="text-sm text-slate-600 mt-1">Remplissez ce formulaire — nous reviendrons vers vous rapidement.</p>
            <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-2"><ShieldCheck className="h-3.5 w-3.5" /> Vos informations sont transmises uniquement à l'équipe RH.</div>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="apply-form">
            {/* Identité */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <label className="block md:col-span-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Titre *</div>
                <select data-testid="apply-civility" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.civility} onChange={(e)=>setForm({...form, civility:e.target.value})}>
                  <option>M.</option><option>Mme</option><option>Autre</option>
                </select>
              </label>
              <label className="block md:col-span-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Prénom *</div>
                <input data-testid="apply-first-name" required className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.first_name} onChange={(e)=>setForm({...form, first_name:e.target.value})} />
              </label>
              <label className="block md:col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Nom *</div>
                <input data-testid="apply-last-name" required className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.last_name} onChange={(e)=>setForm({...form, last_name:e.target.value})} />
              </label>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Email *</div>
                <input data-testid="apply-email" required type="email" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} />
              </label>
              <label className="block">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Téléphone *</div>
                <input data-testid="apply-phone" required className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} placeholder="06 12 34 56 78" />
              </label>
              <label className="block">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Date de naissance *</div>
                <DateInput data-testid="apply-birth-date" required className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.birth_date} onChange={(e)=>setForm({...form, birth_date:e.target.value})} />
              </label>
            </div>

            {/* Adresse */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <label className="block md:col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Adresse postale *</div>
                <input data-testid="apply-address" required className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})} placeholder="12 rue…" />
              </label>
              <label className="block">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Code postal *</div>
                <input data-testid="apply-cp" required className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.postal_code} onChange={(e)=>setForm({...form, postal_code:e.target.value})} />
              </label>
              <label className="block">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Ville *</div>
                <input data-testid="apply-city" required className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.city} onChange={(e)=>setForm({...form, city:e.target.value})} />
              </label>
            </div>

            <label className="block">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Numéro de carte professionnelle *</div>
              <input data-testid="apply-cp-num" required className={`w-full max-w-sm border rounded-md px-3 py-2 text-sm font-mono ${form.carte_pro_number && !CARTE_PRO_RE.test(form.carte_pro_number) ? "border-rose-400 bg-rose-50/40" : "border-slate-300"}`} value={form.carte_pro_number} onChange={(e)=>setForm({...form, carte_pro_number:e.target.value.toUpperCase().replace(/\s+/g, "")})} placeholder="CAR-123-1234-12-12-12341234567" />
              <div className="text-[11px] text-slate-500 mt-1" data-testid="apply-cp-hint">Formats acceptés : <span className="font-mono">1234-1234567-CAR-SH-1234567</span> ou <span className="font-mono">CAR-123-1234-12-12-12341234567</span></div>
              {form.carte_pro_number && !CARTE_PRO_RE.test(form.carte_pro_number) && <div className="text-[11px] text-rose-700 mt-0.5" data-testid="apply-cp-invalid">Format non reconnu.</div>}
            </label>

            {/* Pièces jointes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <UploadField kind="photo" label="Photo d'identité *" accept="image/jpeg,image/png,image/webp" hint="JPG, PNG ou WEBP · 10 Mo max"
                color={color} headers={pf.headers} value={form.photo_file_id} onChange={(id) => setForm((f) => ({ ...f, photo_file_id: id }))} />
              <UploadField kind="cv" label="CV (facultatif)" accept=".pdf,.doc,.docx,image/jpeg,image/png" hint="PDF, Word ou image · 10 Mo max"
                color={color} headers={pf.headers} value={form.cv_file_id} onChange={(id) => setForm((f) => ({ ...f, cv_file_id: id }))} />
            </div>

            {/* Diplômes */}
            <div>
              <div className="text-xs font-bold text-slate-700 mb-2 mt-2">Diplômes</div>
              <div className="space-y-2">
                <Diploma code="SSIAP1" label="Avez-vous le diplôme SSIAP 1 ?" value={form.diplomas.SSIAP1} onChange={(v)=>setD("SSIAP1", v)} />
                <Diploma code="SSIAP2" label="Avez-vous le diplôme SSIAP 2 ?" value={form.diplomas.SSIAP2} onChange={(v)=>setD("SSIAP2", v)} />
                <Diploma code="SSIAP3" label="Avez-vous le diplôme SSIAP 3 ?" value={form.diplomas.SSIAP3} onChange={(v)=>setD("SSIAP3", v)} />
                <Diploma code="SST"    label="Avez-vous le diplôme SST ?"    value={form.diplomas.SST}    onChange={(v)=>setD("SST", v)} />
              </div>
            </div>

            <label className="block">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Message (facultatif)</div>
              <textarea data-testid="apply-message" rows={3} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} placeholder="Disponibilités, mobilité, expériences…" />
            </label>

            <RgpdConsent brand={brand} email={brand.recruitment_email} purpose="la gestion de ma candidature" years={2} checked={form.rgpd_consent} onChange={(v) => setForm({ ...form, rgpd_consent: v })} testid="apply-rgpd" />

            {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}

            <button
              type="submit"
              data-testid="apply-submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: color }}
            >
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi…</> : <><Send className="h-4 w-4" /> Envoyer ma candidature</>}
            </button>
          </form>
        </div>
      </div>

      <style>{`
        .gold-wave {
          background: linear-gradient(
            110deg,
            #b8860b 0%,
            #d4a017 20%,
            #f5d76e 40%,
            #ffe98a 50%,
            #f5d76e 60%,
            #d4a017 80%,
            #b8860b 100%
          );
          background-size: 250% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: goldWave 4s ease-in-out infinite;
          text-shadow: 0 2px 12px rgba(212, 160, 23, 0.25);
          filter: drop-shadow(0 1px 2px rgba(0,0,0,0.35));
        }
        @keyframes goldWave {
          0%   { background-position: 0% center; }
          50%  { background-position: 100% center; }
          100% { background-position: 0% center; }
        }
        @media (prefers-reduced-motion: reduce) {
          .gold-wave { animation: none; }
        }
      `}</style>
      <OwnershipNote info={pf.info} />
      <PublicFooter />
    </div>
  );
}

const CARTE_PRO_RE = /^(CAR-\d{3}-\d{4}-\d{2}-\d{2}-\d{11}|\d{4}-\d{7}-CAR-[A-Z]{2}-\d{7})$/;

function UploadField({ kind, label, accept, hint, color, value, onChange, headers }) {
  const [state, setState] = useState({ busy: false, name: "", preview: null, err: "" });
  const pick = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return setState({ ...state, err: "Fichier trop volumineux (10 Mo max)." });
    setState({ busy: true, name: file.name, preview: kind === "photo" ? URL.createObjectURL(file) : null, err: "" });
    try {
      const fd = new FormData();
      fd.append("kind", kind); fd.append("file", file);
      const r = await axios.post(`${API}/candidates/upload`, fd, { headers });
      onChange(r.data.id);
      setState((s) => ({ ...s, busy: false }));
    } catch (e) {
      onChange(null);
      setState({ busy: false, name: "", preview: null, err: e?.response?.data?.detail || "Échec de l'envoi du fichier." });
    }
  };
  const clear = () => { onChange(null); setState({ busy: false, name: "", preview: null, err: "" }); };
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className={`flex items-center gap-3 rounded-md border-2 border-dashed px-3 py-3 transition-colors ${value ? "border-emerald-300 bg-emerald-50/50" : "border-slate-300 hover:border-slate-400"}`} data-testid={`apply-${kind}-zone`}>
        <div className="h-14 w-12 shrink-0 rounded bg-slate-100 grid place-items-center overflow-hidden">
          {state.preview ? <img src={state.preview} alt="" className="h-full w-full object-cover" /> : kind === "photo" ? <Camera className="h-5 w-5 text-slate-400" /> : <FileText className="h-5 w-5 text-slate-400" />}
        </div>
        <div className="flex-1 min-w-0">
          {state.busy ? <div className="text-xs text-slate-600 flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Envoi…</div>
            : value ? <div className="text-xs font-semibold text-emerald-800 truncate" data-testid={`apply-${kind}-ok`}><CheckCircle2 className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />{state.name}</div>
            : <div className="text-[11px] text-slate-500">{hint}</div>}
          <div className="flex gap-3 mt-1">
            <label className="text-xs font-semibold cursor-pointer hover:underline" style={{ color }}>
              {value ? "Remplacer" : "Choisir un fichier"}
              <input data-testid={`apply-${kind}-input`} type="file" accept={accept} className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} />
            </label>
            {value && <button type="button" onClick={clear} className="text-xs text-slate-500 hover:text-rose-600">Retirer</button>}
          </div>
          {state.err && <div className="text-[11px] text-rose-700 mt-1" data-testid={`apply-${kind}-err`}>{state.err}</div>}
        </div>
      </div>
    </div>
  );
}
